import React, { Component } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug, Copy, Check } from 'lucide-react';

/**
 * Error Boundary with Reporting
 * Captures frontend errors and provides user-friendly error UI
 * Reports errors to backend for monitoring
 */

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      copied: false,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const errorId = `err_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    
    this.setState({
      error,
      errorInfo,
      errorId,
    });

    // Log error to console
    console.error('🚨 Error Boundary Caught:', error, errorInfo);

    // Report error to backend
    this.reportError(error, errorInfo, errorId);

    // Store error locally
    this.storeErrorLocally(error, errorInfo, errorId);
  }

  async reportError(error, errorInfo, errorId) {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const errorReport = {
        errorId,
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo?.componentStack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        userId: user.email || 'anonymous',
        userRole: user.role || 'guest',
        screenSize: `${window.innerWidth}x${window.innerHeight}`,
        localStorage: this.getSafeLocalStorage(),
      };

      // Skip backend reporting if user is not authenticated
      // Error is still logged to console and stored locally
      if (!token) {
        console.log('📤 Error logged locally (user not authenticated):', errorId);
        return;
      }

      await fetch(`${backendUrl}/api/analytics/errors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(errorReport),
      });

      console.log('📤 Error reported to backend:', errorId);
    } catch (e) {
      console.warn('Failed to report error to backend:', e);
    }
  }

  storeErrorLocally(error, errorInfo, errorId) {
    try {
      const errors = JSON.parse(localStorage.getItem('urgaa_errors') || '[]');
      errors.push({
        errorId,
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo?.componentStack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
      });
      
      // Keep only last 50 errors
      if (errors.length > 50) {
        errors.splice(0, errors.length - 50);
      }
      
      localStorage.setItem('urgaa_errors', JSON.stringify(errors));
    } catch (e) {
      console.warn('Failed to store error locally:', e);
    }
  }

  getSafeLocalStorage() {
    try {
      return {
        hasToken: !!localStorage.getItem('token'),
        hasUser: !!localStorage.getItem('user'),
        theme: localStorage.getItem('theme'),
      };
    } catch {
      return {};
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleCopyError = () => {
    const { error, errorInfo, errorId } = this.state;
    const errorText = `
Error ID: ${errorId}
Message: ${error?.message}
URL: ${window.location.href}
Time: ${new Date().toISOString()}

Stack Trace:
${error?.stack}

Component Stack:
${errorInfo?.componentStack}
    `.trim();

    navigator.clipboard.writeText(errorText).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    });
  };

  render() {
    if (this.state.hasError) {
      const { error, errorId, copied } = this.state;

      return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
          <div className="max-w-lg w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
            {/* Error Icon */}
            <div className="mx-auto w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-6">
              <AlertTriangle className="w-10 h-10 text-red-600 dark:text-red-400" />
            </div>

            {/* Error Title */}
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Something went wrong
            </h1>
            
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              We've encountered an unexpected error. Our team has been notified and is working on a fix.
            </p>

            {/* Error ID */}
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3 mb-6">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Error Reference</p>
              <code className="text-sm font-mono text-gray-800 dark:text-gray-200">
                {errorId}
              </code>
            </div>

            {/* Error Details (collapsible) */}
            <details className="text-left mb-6 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <Bug size={16} />
                Technical Details
              </summary>
              <div className="mt-3 space-y-2">
                <p className="text-xs text-red-600 dark:text-red-400 font-mono break-all">
                  {error?.message}
                </p>
                <pre className="text-xs text-gray-500 dark:text-gray-400 overflow-auto max-h-32 bg-gray-100 dark:bg-gray-800 p-2 rounded">
                  {error?.stack?.split('\n').slice(0, 5).join('\n')}
                </pre>
              </div>
            </details>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={this.handleReload}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors"
              >
                <RefreshCw size={18} />
                Reload Page
              </button>
              
              <button
                onClick={this.handleGoHome}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white rounded-xl font-medium transition-colors"
              >
                <Home size={18} />
                Go Home
              </button>
            </div>

            {/* Copy Error Button */}
            <button
              onClick={this.handleCopyError}
              className="mt-4 flex items-center justify-center gap-2 mx-auto text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              {copied ? (
                <>
                  <Check size={14} className="text-green-500" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy size={14} />
                  Copy error details
                </>
              )}
            </button>

            {/* Support Info */}
            <p className="mt-6 text-xs text-gray-400 dark:text-gray-500">
              If this problem persists, please contact{' '}
              <a href="mailto:support@go4garage.in" className="text-blue-500 hover:underline">
                support@go4garage.in
              </a>
              {' '}with the error reference above.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
